// rtk-ingest: consume `rtk discover` (repeated shell commands with estimated
// token savings) into the suggestion engine as command-level suggestions.
//
// Distinct from `cai learn` (prompt corrections) and `cai pattern recurring`
// (commit clusters): this is shell-command savings from the external rtk tool.
// No-op when rtk is not installed — the provider is additive, never required.

import { spawnSync } from "node:child_process";
import type { Suggestion, SuggestContext, SuggestionProvider } from "../../types.js";
import { parseRtkDiscover, type RtkFinding } from "../rtk.js";

/** Run `rtk discover` and return its raw text, or null if rtk is unavailable. */
function runRtkDiscover(): string | null {
  try {
    const result = spawnSync("rtk", ["discover"], { encoding: "utf-8", timeout: 15_000 });
    if (result.error || result.status !== 0 || !result.stdout) return null;
    return result.stdout;
  } catch {
    return null;
  }
}

function findingToSuggestion(f: RtkFinding): Suggestion {
  return {
    kind: "command-skill",
    title: `Use \`${f.equivalent}\` for \`${f.command}\``,
    rationale: `\`${f.command}\` ran ${f.count}× recently; \`${f.equivalent}\` is the token-leaner equivalent (rtk).`,
    estimatedSaving: f.estSaving,
    files: [],
  };
}

export function runRtkIngest(_ctx: SuggestContext): Suggestion[] {
  const output = runRtkDiscover();
  if (!output) return [];
  return parseRtkDiscover(output).map(findingToSuggestion);
}

export const rtkIngestProvider: SuggestionProvider = {
  name: "rtk-ingest",
  run: runRtkIngest,
};
